/**
 * 常用句收藏
 * ============================================================
 * 场景：旅行时把翻译好的句子存下来，随时翻出来用。
 *
 * ── 离线能力的真实边界（必须说清楚）────────────────────────
 *   文字（方言原句 / 注音 / 回译）→ localStorage → **真正离线可用**
 *   音频                          → 首次播放后缓存到 IndexedDB → **可离线播放**
 *   未缓存过的音频                 → 需要联网合成
 *
 *   所以：**收藏时不会自动生成音频**（那会产生大量请求且拖慢保存），
 *   而是"听过的就留下"。界面上会区分「已缓存·可离线」与「需联网」。
 *
 * ── 为什么音频用 IndexedDB 而不是 localStorage ──────────────
 *   localStorage 只能存字符串，且总容量约 5MB。
 *   一段 TTS 音频约 80-170KB，存成 base64 还会膨胀 33% ——
 *   十几句就把配额撑爆，且同步写入会卡住主线程。
 *   IndexedDB 能直接存 Blob，容量大得多，且是异步的。
 */

const LS_KEY = 'dialectgo_saved_phrases';
const DB_NAME = 'dialectgo_offline';
const DB_VERSION = 1;
const AUDIO_STORE = 'tts-audio';

/* ============================================================
 * 收藏条目（localStorage）
 * ============================================================ */

/**
 * 生成稳定 id
 * 用「方言 + 原句」做键，避免同一句重复收藏
 */
function makeId(dialectId, dialectText) {
  return `${dialectId}::${String(dialectText || '').trim()}`;
}

/** 读取全部收藏（按收藏时间倒序） */
export function getSavedPhrases() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const j = JSON.parse(raw);
    if (!Array.isArray(j)) return [];
    return j.sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));
  } catch {
    return [];
  }
}

/** 是否已收藏 */
export function isSaved(dialectId, dialectText) {
  const id = makeId(dialectId, dialectText);
  return getSavedPhrases().some((p) => p.id === id);
}

/**
 * 收藏一句
 * @returns {{ok:boolean, id?:string, duplicate?:boolean, error?:string}}
 */
export function savePhrase(item) {
  const dialectText = String(item?.dialectText || '').trim();
  if (!dialectText) return { ok: false, error: '没有可收藏的内容' };

  const dialectId = item.dialectId || 'yue-HK';
  const id = makeId(dialectId, dialectText);
  const list = getSavedPhrases();

  if (list.some((p) => p.id === id)) {
    return { ok: true, id, duplicate: true };
  }

  const entry = {
    id,
    dialectId,
    dialectName: item.dialectName || '',
    dialectText,
    romanization: item.romanization || '',
    source: item.source || '', // 原普通话
    backTranslation: item.backTranslation || '',
    notes: item.notes || '',
    uncertain: !!item.uncertain,
    savedAt: Date.now(),
  };

  try {
    localStorage.setItem(LS_KEY, JSON.stringify([entry, ...list]));
    return { ok: true, id };
  } catch (e) {
    // 配额满时要明确告知，不能静默失败
    return { ok: false, error: '本地存储已满，请先删除一些收藏' };
  }
}

/** 取消收藏（同时删掉缓存的音频，避免留下孤儿数据） */
export function removePhrase(id) {
  const list = getSavedPhrases();
  const next = list.filter((p) => p.id !== id);
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(next));
  } catch {
    return { ok: false };
  }
  deleteAudio(id).catch(() => {});
  return { ok: true, removed: list.length !== next.length };
}

/** 清空收藏（连同音频缓存） */
export function clearSavedPhrases() {
  try {
    localStorage.removeItem(LS_KEY);
  } catch {
    /* 忽略 */
  }
  clearAllAudio().catch(() => {});
}

/* ============================================================
 * 音频缓存（IndexedDB）
 * ============================================================ */

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    try {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(AUDIO_STORE)) {
          db.createObjectStore(AUDIO_STORE);
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null); // 降级：不能因为缓存不可用就崩
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

/** 缓存一段音频 */
export async function putAudio(id, blob) {
  const db = await openDb();
  if (!db || !blob) return false;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(AUDIO_STORE, 'readwrite');
      tx.objectStore(AUDIO_STORE).put({ blob, cachedAt: Date.now() }, id);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    } catch {
      resolve(false);
    }
  });
}

/** 取缓存的音频（返回 Blob 或 null） */
export async function getAudio(id) {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(AUDIO_STORE, 'readonly');
      const req = tx.objectStore(AUDIO_STORE).get(id);
      req.onsuccess = () => resolve(req.result?.blob || null);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

/** 删除某条音频 */
export async function deleteAudio(id) {
  const db = await openDb();
  if (!db) return false;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(AUDIO_STORE, 'readwrite');
      tx.objectStore(AUDIO_STORE).delete(id);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    } catch {
      resolve(false);
    }
  });
}

/** 清空全部音频缓存 */
export async function clearAllAudio() {
  const db = await openDb();
  if (!db) return false;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(AUDIO_STORE, 'readwrite');
      tx.objectStore(AUDIO_STORE).clear();
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    } catch {
      resolve(false);
    }
  });
}

/**
 * 批量查哪些收藏已有离线音频
 * @returns {Promise<Set<string>>} 已缓存条目的 id 集合
 */
export async function getCachedIds(ids) {
  const db = await openDb();
  if (!db) return new Set();
  return new Promise((resolve) => {
    const out = new Set();
    try {
      const tx = db.transaction(AUDIO_STORE, 'readonly');
      const store = tx.objectStore(AUDIO_STORE);
      let pending = ids.length;
      if (!pending) {
        resolve(out);
        return;
      }
      ids.forEach((id) => {
        const req = store.get(id);
        req.onsuccess = () => {
          if (req.result) out.add(id);
          pending -= 1;
          if (pending === 0) resolve(out);
        };
        req.onerror = () => {
          pending -= 1;
          if (pending === 0) resolve(out);
        };
      });
    } catch {
      resolve(out);
    }
  });
}

/** 估算音频缓存占用（字节） */
export async function estimateAudioBytes() {
  const db = await openDb();
  if (!db) return 0;
  return new Promise((resolve) => {
    let total = 0;
    try {
      const tx = db.transaction(AUDIO_STORE, 'readonly');
      const req = tx.objectStore(AUDIO_STORE).openCursor();
      req.onsuccess = () => {
        const cur = req.result;
        if (cur) {
          total += cur.value?.blob?.size || 0;
          cur.continue();
        } else {
          resolve(total);
        }
      };
      req.onerror = () => resolve(0);
    } catch {
      resolve(0);
    }
  });
}

export const _internal = { makeId, LS_KEY, AUDIO_STORE };
